import { TitleCasePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { ColDef } from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { mapTransactionType, mapTweRecurringTransactionType, sharedColNames } from '../../../create-request/components/transactions-table/table-helpers';
import { CashieringRequestApr } from '../../../create-request/models/internal-transfer/CashieringRequestApr';
import { RecurringRequest } from '../../../create-request/models/internal-transfer/RecurringRequest';
import { TweRecurringTableColumn, TweStandingAuthTableColumn, TweTransactionsTableColumn } from '../../types/TransactionTableColumn';
import { FormatDatePipe } from '../../../shared/pipes/FormatDatePipe';
import { TableComponentState } from '../../types/TableComponentState';
import { gridReady } from '../../../create-request/actions/transactions-table-actions';
import { amountFormatter } from '../../utilities/AmountFormatter';
import { MediaTypeCdDict, transactionDirection, transactionTableTabs, tweTransactionTypeDict } from '../../utilities/Constants';
import { SloaRequest } from '../../../create-request/models/internal-transfer/SloaRequest';
import { NamePipe } from '../../pipes/NamePipe';

@Component({
  standalone: false,
  selector: 'mm-twe-modal-tables',
  templateUrl: './twe-modal-tables.component.html',
  styleUrls: ['./twe-modal-tables.component.scss']
})
export class TweModalTablesComponent implements OnInit {
  @Input() upcomingTransactions$: Observable<(CashieringRequestApr | RecurringRequest)[]>;
  @Input() sloas$: Observable<SloaRequest[]>;
  @Input() recurringTransactions$: Observable<RecurringRequest[]>;
  @Input() tweTableState$: Observable<TableComponentState>;
  destroy$: Subject<boolean> = new Subject<boolean>();
  rowData$: Observable<TweTransactionsTableColumn[] | TweRecurringTableColumn[] | TweStandingAuthTableColumn[]>;
  formatDatePipe = new FormatDatePipe();
  columnDefs: ColDef[];
  upcomingColumnDefs: ColDef[] = [
    {
      field: 'Type',
      sortable: false
    },
    {
      field: sharedColNames.toFromAccount,
      sortable: false
    },
    {
      field: 'Amount',
      sortable: false,
      type: 'rightAligned'
    },
    {
      field: 'Process date',
      sortable: false
    },
  ];

  standingAuthColumnDefs: ColDef[] = [
    {
      field: 'Type',
      sortable: false
    },
    {
      field: sharedColNames.toFromAccount,
      sortable: false
    },
    {
      field: 'Transfer Direction',
      sortable: false
    },
  ];

  recurringColumnDefs: ColDef[] = [
    {
      field: 'Type',
      sortable: false
    },
    {
      field: sharedColNames.toFromAccount,
      sortable: false
    },
    {
      field: 'Amount',
      sortable: false,
      type: 'rightAligned'
    },
    {
      field: 'Next process date',
      sortable: false
    },
    {
      field: 'Frequency',
      sortable: false
    },
  ];

  constructor(private store: Store, private namePipe: NamePipe, private titleCasePipe: TitleCasePipe) { }

  ngOnInit(): void {
    this.columnDefs = this.upcomingColumnDefs;
    this.rowData$ = this.upcomingTransactions$.pipe(map(transactions => this.mapUpcomingTransactionsToRowData(transactions)));

    this.setActiveTableEventListener();
  }

  updateGridReady(ready: boolean) {
    this.store.dispatch(gridReady({ gridReady: ready, tab: transactionTableTabs.all }));
  }

  mapUpcomingTransactionsToRowData(transactions: (CashieringRequestApr | RecurringRequest)[]): TweTransactionsTableColumn[] {
    return transactions.map((transaction) => {
      return this.mapUpcomingTransactions(transaction)
    });
  }

  mapUpcomingTransactions(transaction: (CashieringRequestApr | RecurringRequest)): TweTransactionsTableColumn {
    if (this.instanceOfCashiering(transaction)) {
      return {
        Type: mapTransactionType(
          transaction.typeDescription,
          transaction.direction === 'O' ? transactionDirection.out : transactionDirection.in
        ),
        Amount: amountFormatter.format(transaction.cashrRequestGrossAmount),
        'To/From': this.titleCasePipe.transform(
          `${transaction.toPart1} ${transaction.toPart2} ${transaction.toPart3}`
        ),
        'Process date': transaction.typeDescription.includes('Recurring') ? '' : this.formatDatePipe.transform(transaction.formattedDate)
      };
    }
    else {
      return {
        Type: mapTransactionType(
          tweTransactionTypeDict[transaction.recurMediaTypeCode],
          transaction.recurDirection === 'O' ? transactionDirection.out : transactionDirection.in
        ),
        Amount: amountFormatter.format(transaction.recurRequestGrossAmount),
        'To/From': this.titleCasePipe.transform(
          `${transaction.recurToPart1} ${transaction.recurToPart2} ${transaction.recurToPart3}`
        ),
        'Process date': this.formatDatePipe.transform(transaction.formattedDate)
      };
    }
  }

  instanceOfCashiering(transaction: CashieringRequestApr | RecurringRequest): transaction is CashieringRequestApr {
    return ('cashrRequestId' in transaction);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private setActiveTableEventListener(): void {
    const activeTables = document.getElementsByClassName('twe-table-tabs');

    for (var i =0; i < activeTables.length; i++) {
      activeTables[i].addEventListener(
        'sdpsSelect',
        ((event: Event) => {
          // sdpsSelect is a custom Event from Everest
          // e.detail contains Id of sdpsSelct
          const e = event as CustomEvent;
          switch (e.detail as string) {
            case 'upcoming':
              this.rowData$ = this.upcomingTransactions$
                .pipe(map((transactions) => this.mapUpcomingTransactionsToRowData(transactions)));
              this.columnDefs = this.upcomingColumnDefs;
              break;
            case 'standingAuth':
              this.rowData$ = this.sloas$
                .pipe(map((transactions) => this.mapStandingAuthorizationsToRowData(transactions)));
              this.columnDefs = this.standingAuthColumnDefs;
              break;
            case 'recurring':
              this.rowData$ = this.recurringTransactions$
                .pipe(map((transactions) => this.mapRecurringTransactionsToRowData(transactions)));
              this.columnDefs = this.recurringColumnDefs;
              break;
            default:
              this.columnDefs = this.upcomingColumnDefs;
          }
        }
      ));
    }
  }

  mapRecurringTransactionsToRowData(transactions: RecurringRequest[]): TweRecurringTableColumn[] {
    let recurringTransactions: TweRecurringTableColumn[] = [];
    transactions.forEach(transaction => {
      const trans: TweRecurringTableColumn = {
        Type: mapTweRecurringTransactionType(transaction.recurMediaTypeCode),
        'To/From': this.namePipe.transform(
          `${transaction.recurToPart1} ${transaction.recurToPart2} ${transaction.recurToPart3}`
        ),
        Amount: amountFormatter.format(transaction.recurRequestGrossAmount),
        'Next process date': this.formatDatePipe.transform(transaction.formattedDate),
        Frequency: transaction.frequencyDescription
      }
      recurringTransactions.push(trans);
    });
    return recurringTransactions;
  }

  mapStandingAuthorizationsToRowData(authorizations: SloaRequest[]): TweStandingAuthTableColumn[] {
    let standingAuths: TweStandingAuthTableColumn[] = authorizations.map((authorization) => {
      return {
        'Type': this.titleCasePipe.transform(MediaTypeCdDict[authorization.sloaMediaTypeCode]),
        'To/From': this.namePipe.transform(`${authorization.sloaToPart1} ${authorization.sloaToPart2} ${authorization.sloaToPart3}`),
        'Transfer Direction': authorization.sloaDirection === 'I' ? 'Incoming' : 'Outgoing'
      }
    });

    return standingAuths;
  }
}
